import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import ProductCard from "@/components/ProductCard";
import { Loader2, Sparkles, Clock, PackageCheck } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export default function PrepTimeline() {
  const { profile, addToWishlist, inWishlist } = useProfile();
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;
    setLoading(true);
    api.get("/catalog/recommended-timeline", { params: { profile_id: profile.id } })
      .then(({ data }) => setBuckets(data.buckets || []))
      .catch(() => setBuckets([]))
      .finally(() => setLoading(false));
  }, [profile]);

  if (!profile) return null;

  return (
    <section id="preparare" className="bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-20">
        <div className="flex items-center gap-2 text-[#C87D75] mb-2">
          <PackageCheck size={18} />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">Cosa preparare · AI</span>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825]">Cosa preparare, passo dopo passo</h2>
        <p className="text-[#5C534E] mt-2 max-w-xl mb-8">
          In base alla data prevista, alla stagione e al clima di {profile.city}: dalla taglia nascita 0-3
          fino ai primi mesi, ecco cosa mettere in valigia e cosa preparare dopo.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#8C7F78] py-12 justify-center" data-testid="prep-loading">
            <Loader2 className="animate-spin" size={18} /> Sto preparando i consigli…
          </div>
        ) : buckets.length === 0 ? (
          <p className="text-sm text-[#8C7F78] py-8" data-testid="prep-empty">Nessun consiglio disponibile al momento.</p>
        ) : (
          <Accordion type="single" collapsible defaultValue={String(buckets[0].offset)} className="space-y-3" data-testid="prep-timeline">
            {buckets.map((bk, bi) => (
              <AccordionItem key={bk.offset} value={String(bk.offset)} data-testid={`prep-bucket-${bk.offset}`}
                className="rounded-2xl border border-[#EAE3DC] bg-white px-4 sm:px-6">
                <AccordionTrigger className="hover:no-underline py-4" data-testid={`prep-toggle-${bk.offset}`}>
                  <div className="flex flex-wrap items-center gap-2 text-left">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2D2825] text-white text-sm px-4 py-1.5">
                      {bi === 0 ? <Sparkles size={14} /> : <Clock size={14} />} {bk.label}
                    </span>
                    <span className="rounded-full bg-[#EAF0EC] text-[#4E7363] text-xs px-3 py-1">{bk.season}{bk.avg_temp != null ? ` · ~${bk.avg_temp}°C` : ""}</span>
                    <span className="rounded-full bg-[#F9ECE9] text-[#C87D75] text-xs px-3 py-1">Taglia {bk.size}</span>
                    <span className="text-xs text-[#8C7F78] font-normal">{bk.products.length} capi</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-[#5C534E] mb-4">{bk.note}</p>
                  {bk.products.length === 0 ? (
                    <p className="text-xs text-[#8C7F78]">Nessun capo adatto disponibile per questo periodo.</p>
                  ) : (
                    <div className="mb-rail sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 pb-2">
                      {bk.products.map((p, i) => (
                        <ProductCard key={p.id} product={p} delay={i * 0.04}
                          onAdd={() => addToWishlist(p)} inList={inWishlist(p.id)}
                          testid={`prep-product-${bk.offset}-${p.id}`} />
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </section>
  );
}
